import { Injectable } from "@angular/core";

import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { ImportFileParser } from "../parsers/pbm/beneficiary/import-file.parser";
import { ImportBeneficiaryResult } from "../../models/domains/pbm/Beneficiary/import-beneficiary-result";
import { FileBeneficiary } from "../../models/domains/pbm/Beneficiary/file-beneficiary";
import { BackendUrl } from "../../models/domains/urls/back-end.url";
import { PagingRequest } from "../../models/domains/paginator/paging-request";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { BeneficaryTemporaryLimit, Beneficiary } from "../../models/domains/pbm/Beneficiary/beneficiary";
import { SaveBenficiaryParser } from "../parsers/pbm/beneficiary/save-beneficiary.parser";
import { BeneficiaryKey } from "../../models/domains/pbm/Benefit/beneficiary-key";
import { FindBeneficiaryHolderBySocialNumberParser } from "../parsers/pbm/beneficiary/find-beneficiary-by-holder-social-number.parser";
import { FindBeneficiariesByFiscalNumberParser } from "../parsers/pbm/beneficiary/find-beneficiaries-by-fiscal-number.parser";
import { Dependent } from "../../models/domains/pbm/Beneficiary/dependent";
import { FindBeneficiaryByKeyParser } from "../parsers/pbm/beneficiary/find-beneficiary-by-key.parser";
import { FindBeneficiaryBySocialNumberParser } from "../parsers/pbm/beneficiary/find-beneficiary-by-social-number.parser";
import { ExportOrderKey } from "../../models/domains/pbm/exports/export-order-key";
import { CardBlockDTO } from "../../models/domains/pbm/Beneficiary/dto/card-block-dto";
import { BlockCardReason } from "../../models/domains/pbm/Beneficiary/block-card-reason";
import { BlockReasonListParser } from "../parsers/pbm/beneficiary/block-reason-list.parser";
import { CardUnblockDTO } from "../../models/domains/pbm/Beneficiary/dto/card-unblock-dto";
import { OrderBeneficiaryCardParser } from "../parsers/pbm/beneficiary/order-beneficiary-card.parser";
import { OrderCardDTO } from "../../models/domains/pbm/Beneficiary/dto/order-card-dto";
import { UserRoleMediator } from "../mediators/user-role.mediator";
import { FiscalNumber } from "../../models/domains/pbm/fiscal-number";
import { BeneficiarySearchParameterDTO } from "../../models/domains/pbm/Beneficiary/dto/beneficiary-search-parameter-dto";
import { BeneficiaryDTO, BeneficiaryResultDTO } from "../../models/domains/pbm/Beneficiary/dto/beneficiary-dto";



@Injectable()
export class BeneficiaryRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private importFileParser: ImportFileParser,
    private saveBenficiaryParser: SaveBenficiaryParser,
    private findBeneficiaryByKeyParser: FindBeneficiaryByKeyParser,
    private findBeneficiariesByFiscalNumberParser: FindBeneficiariesByFiscalNumberParser,
    private findBeneficiaryHolderBySocialNumberParser: FindBeneficiaryHolderBySocialNumberParser,
    private findBeneficiaryBySocialNumberParser: FindBeneficiaryBySocialNumberParser,
    private blockReasonListParser: BlockReasonListParser,
    private orderBeneficiaryCardParser: OrderBeneficiaryCardParser,
    private userRoleMediator: UserRoleMediator,
  ) { }

  public async importBeneficiary(fileBeneficiary: FileBeneficiary, fiscalNumber: FiscalNumber): Promise<string> {
    try {
      const exportKey = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithFile(
          new BackendUrl(`/api/beneficiary/import/${fiscalNumber.getValue()}`),
          fileBeneficiary.getFile(),
        ) as string;
      return await Promise.resolve(exportKey);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async importBlockCardNumber(fileBeneficiary: FileBeneficiary): Promise<ImportBeneficiaryResult> {
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithFile(new BackendUrl("/api/beneficiary/card/block/import/" + fileBeneficiary.getFiscalNumber()), fileBeneficiary.getFile());
      return await Promise.resolve(this.importFileParser.parse(data));
    } catch (error) {
      return await Promise.reject(this.importFileParser.parse(error));
    }
  }

  public async importUnblockCardNumber(fileBeneficiary: FileBeneficiary): Promise<ImportBeneficiaryResult> {
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithFile(new BackendUrl("/api/beneficiary/card/unblock/import/" + fileBeneficiary.getFiscalNumber()), fileBeneficiary.getFile());
      return await Promise.resolve(this.importFileParser.parse(data));
    } catch (error) {
      return await Promise.reject(this.importFileParser.parse(error));
    }
  }

  public async exportBeneficiaryUsageFileByCardNumber(cardNumber: string) {
    const headers = { "Content-Type": "application/json" };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      return await remoteGateway.downloadAsGet(new BackendUrl("/api/financial-statements/card/usage/" + cardNumber), headers);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findBeneficiariesByFiscalNumber(fiscalNumber: string, pagingRequest: PagingRequest, isDependent: boolean = null): Promise<PageableResult> {
    const body = JSON.stringify({
      StartIndex: pagingRequest.getStart(),
      TotalPerPages: pagingRequest.getEnd(),
      FiscalNumber: fiscalNumber,
      FilterText: pagingRequest.getFilterText(),
      IsDependent: isDependent
    });

    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/beneficiaries"), body, headers);
      return await Promise.resolve(this.findBeneficiariesByFiscalNumberParser.parse(data));
    } catch (httpErrorResponse) {
      if (httpErrorResponse.error) {
        return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
      } else if (httpErrorResponse.status == 404) {
        return await Promise.reject("Beneficiário não encontrado");
      }
    }
  }

  public async SearchBeneficiaries(params: BeneficiarySearchParameterDTO): Promise<BeneficiaryResultDTO> {
    const headers = {
      "Content-Type": "application/json"
    };

    const body = JSON.stringify(params)
    try {
      const data = await this.remoteGatewayFactory
        .createAuthRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/search/beneficiarySocialNumber/export"), body, headers)
      return await Promise.resolve(data as BeneficiaryResultDTO)
    } catch (httpErrorResponse) {
      const errorMessage = httpErrorResponse.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async findBeneficiaryByKey(beneficiaryKey: BeneficiaryKey): Promise<Beneficiary> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .getWithHeaders(new BackendUrl("/api/beneficiary/" + beneficiaryKey.getValue()), headers);
      return await Promise.resolve(this.findBeneficiaryByKeyParser.parse(data));
    } catch (httpErrorResponse) {
      if (httpErrorResponse.error) {
        return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
      } else if (httpErrorResponse.status == 404) {
        return await Promise.reject("Beneficiário não encontrado");
      }
    }
  }

  public async findBeneficiaryHolderBySocialNumber(socialNumber: string, fiscalNumber: string): Promise<Beneficiary> {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify({
      "fiscalNumber": {
        "value": fiscalNumber
      },
      "socialNumber": {
        "value": socialNumber
      }
    });

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/holder"), body, headers);
      return this.findBeneficiaryHolderBySocialNumberParser.parse(data);
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async findBeneficiaryBySocialNumber(socialNumber: string): Promise<Beneficiary | Dependent> {
    const headers = {
      "Content-Type": "application/json",
    };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .getWithHeaders(new BackendUrl("/api/beneficiary/" + socialNumber), headers);
      return this.findBeneficiaryBySocialNumberParser.parse(data);
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async saveBeneficiary(fiscalNumber: string, beneficiary: Beneficiary): Promise<BeneficiaryKey> {
    const dependents = beneficiary.getDependets().map((dependent) => {
      return {
        BeneficiaryKey: dependent.getBeneficiaryKey(),
        BeneficiarySocialNumber: beneficiary.getSocialNumber().replace(/[^\d]+/g, ""),
        Card: {
          CardNumber: beneficiary.getCard().getCardNumber(),
        },
        Name: dependent.getName(),
        SocialNumber: dependent.getSocialNumber().replace(/[^\d]+/g, ""),
        BirthDate: dependent.getBirthDate().replace(/[^\d]+/g, ""),
        Address: {
          IsNewAddress: dependent.getAddress().getIsNewAddress(),
          Street: dependent.getAddress().getStreet(),
          Number: dependent.getAddress().getNumber(),
          Complement: dependent.getAddress().getComplement(),
          ZipCode: dependent.getAddress().getZipCode(),
          District: dependent.getAddress().getDistrict(),
          City: dependent.getAddress().getCity(),
          Region: dependent.getAddress().getRegion(),
        },
        Email: dependent.getEmail(),
        Gender: dependent.getGender().getValue(),
        MaritalStatus: dependent.getMaritalStatus().getValue(),
        Telephone: dependent.getTelephone().replace(/[^\d]+/g, ""),
        Mobile: dependent.getMobile().replace(/[^\d]+/g, ""),
        NeedValidateEmail: dependent.getNeedValidateEmail(),
        EnableDelivery: dependent.getEnableDelivery(),
        TransactionPasswordRequired: dependent.getTransactionPasswordRequired(),
      };
    });

    const body = JSON.stringify({
      Employee: {
        Registration: beneficiary.getEmployee().getRegistration(),
        AdmissionDate: beneficiary
          .getEmployee()
          .getAdmissionDate()
          .replace(/[^\d]+/g, ""),
        JobDescription: beneficiary.getEmployee().getJobDescription()
          ? {
            Key: {
              Value: beneficiary.getEmployee().getJobDescription().getKey(),
            },
            Title: beneficiary.getEmployee().getJobDescription().getTitle(),
            IsGeneric: beneficiary.getEmployee().getJobDescription().getIsGeneric(),
            Enabled: beneficiary.getEmployee().getJobDescription().getEnabled(),
          }
          : null,
      },
      Active: beneficiary.isActive(),
      BeneficiaryKey: beneficiary.getBeneficiaryKey(),
      Dependents: dependents,
      Card: {
        CardNumber: beneficiary.getCard().getCardNumber(),
      },
      Name: beneficiary.getName(),
      SocialNumber: beneficiary.getSocialNumber().replace(/[^\d]+/g, ""),
      BirthDate: beneficiary.getBirthDate().replace(/[^\d]+/g, ""),
      Address: {
        Street: beneficiary.getAddress().getStreet(),
        Number: beneficiary.getAddress().getNumber(),
        Complement: beneficiary.getAddress().getComplement(),
        ZipCode: beneficiary
          .getAddress()
          .getZipCode()
          .replace(/[^\d]+/g, ""),
        District: beneficiary.getAddress().getDistrict(),
        City: beneficiary.getAddress().getCity(),
        Region: beneficiary.getAddress().getRegion(),
      },
      Email: beneficiary.getEmail(),
      Gender: beneficiary.getGender().getValue(),
      MaritalStatus: beneficiary.getMaritalStatus().getValue(),
      Telephone: beneficiary.getTelephone().replace(/[^\d]+/g, ""),
      Mobile: beneficiary.getMobile().replace(/[^\d]+/g, ""),
      NeedValidateEmail: beneficiary.getNeedValidateEmail(),
      EnableDelivery: beneficiary.getEnableDelivery(),
      TransactionPasswordRequired: beneficiary.getTransactionPasswordRequired(),
    });

    const headers = { "Content-Type": "application/json" };
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/" + fiscalNumber), body, headers);
      return await Promise.resolve(this.saveBenficiaryParser.parse(data));
    } catch (error) {
      return await Promise.reject(this.saveBenficiaryParser.parse(error));
    }
  }

  public async findBeneficiaryExportOrderKey(fiscalNumber: string): Promise<ExportOrderKey> {
    const headers = {
      "Content-Type": "application/json",
    };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .getWithHeaders(new BackendUrl("/api/beneficiary/export/" + fiscalNumber), headers);
      const exportOrderKey = new ExportOrderKey(data.toString());
      return exportOrderKey;
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async ExporBeneficiaries(beneficiries: BeneficiarySearchParameterDTO): Promise<ExportOrderKey> {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify(beneficiries)

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/export-beneficiaries"), body, headers);
      const exportOrderKey = new ExportOrderKey(data.toString());
      return exportOrderKey;
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async cancelBeneficiaryCard(beneficiaryKey: BeneficiaryKey): Promise<any> {
    const headers = { "Content-Type": "application/json" };

    const body = JSON.stringify({
      Value: beneficiaryKey.getValue(),
    });

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/card/cancel"), body, headers);
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async orderBeneficiaryCard(beneficiaryKey: BeneficiaryKey): Promise<OrderCardDTO> {
    const headers = { "Content-Type": "application/json" };

    const body = JSON.stringify({
      BeneficiaryKey: {
        Value: beneficiaryKey.getValue(),
      },
      Source: "Portal",
    });

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/card/order"), body, headers);
      return await Promise.resolve(this.orderBeneficiaryCardParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getBlockReasonList(): Promise<BlockCardReason[]> {
    const headers = { "Content-Type": "application/json" };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .getWithHeaders(new BackendUrl("/api/beneficiary/card/block/reasons/Portal"), headers);
      return await Promise.resolve(this.blockReasonListParser.parse(data));
    } catch (error) {
      return await Promise.reject(this.blockReasonListParser.parse(error));
    }
  }

  public async blockBeneficiaryCard(cardBlockDTO: CardBlockDTO): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    };

    const body = JSON.stringify(cardBlockDTO);

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/card/block"), body, headers);
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async unblockBeneficiaryCard(cardUnblockDTO: CardUnblockDTO): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    };

    const body = JSON.stringify(cardUnblockDTO);

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/beneficiary/card/unblock"), body, headers);
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }


  public async beneficaryTemporaryLimit(temporaryLimit: BeneficaryTemporaryLimit): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    }

    const body = JSON.stringify(temporaryLimit);

    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().putWithHeaders(new BackendUrl("/api/benefit/configurations/beneficiary/cycle/limit"), body, headers);
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error)
    }

  }

}
